import { FC, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import EXTRA_QUESTION_LABELS from '../../../../constants/extraQuestions'
import { Radio, RadioGroup } from '../../../../shared/compass-ui'
import HiddenFieldMirror from '../../../shared/HiddenFieldMirror'
import TextInputError from '../../../shared/TextInputError'

const BNPL_FREQUENCY_OPTIONS = [
  { label: 'Never', value: 'never' },
  { label: 'A few times a year', value: 'few_times_year' },
  { label: 'About once a month', value: 'once_month' },
  { label: 'Multiple times a month', value: 'multiple_times_month' },
]

const BNPL_PURCHASE_TYPE_OPTIONS = [
  { label: 'Purchases under $50', value: 'under_50' },
  { label: 'Purchases of $50 – $250', value: '50_250' },
  { label: 'Purchases of $250 – $1,000', value: '250_1000' },
  { label: 'Purchases over $1,000', value: 'over_1000' },
]

const BNPL_LATE_PAYMENTS_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
]

const BnplDetails: FC = () => {
  const {
    formState: {
      errors: {
        bnplFrequency: bnplFrequencyError,
        bnplPurchaseType: bnplPurchaseTypeError,
        bnplLatePayments: bnplLatePaymentsError,
      },
    },
    unregister,
  } = useFormContext()

  const bnplFrequencyValue = useWatch({ name: 'bnplFrequency' })
  const bnplPurchaseTypeValue = useWatch({ name: 'bnplPurchaseType' })
  const bnplLatePaymentsValue = useWatch({ name: 'bnplLatePayments' })
  const showConditionalFields = !!bnplFrequencyValue && bnplFrequencyValue !== 'never'

  useEffect(() => {
    if (!showConditionalFields) {
      unregister(['bnplPurchaseType', 'bnplLatePayments'])
    }
  }, [showConditionalFields, unregister])

  const hasFrequencyError = typeof bnplFrequencyError?.message === 'string'
  const hasPurchaseTypeError = typeof bnplPurchaseTypeError?.message === 'string'
  const hasLatePaymentsError = typeof bnplLatePaymentsError?.message === 'string'

  return (
    <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-6">
      <HiddenFieldMirror name="bnplFrequency" />
      <HiddenFieldMirror name="bnplPurchaseType" />
      <HiddenFieldMirror name="bnplLatePayments" />
      <div>
        <RadioGroup
          label={EXTRA_QUESTION_LABELS.bnplFrequency}
          hasError={hasFrequencyError}
          id="bnpl-frequency-radio-group"
        >
          {BNPL_FREQUENCY_OPTIONS.map((option, idx) => (
            <Radio
              key={`bnpl-frequency-${option.value}`}
              {...option}
              idx={idx}
              hasError={hasFrequencyError}
              name="bnplFrequency"
              selectedValue={bnplFrequencyValue ?? null}
            />
          ))}
        </RadioGroup>
        {hasFrequencyError && <TextInputError>This field is required</TextInputError>}
      </div>
      {showConditionalFields && (
        <>
          <div>
            <RadioGroup
              label={EXTRA_QUESTION_LABELS.bnplPurchaseType}
              hasError={hasPurchaseTypeError}
              id="bnpl-purchase-type-radio-group"
            >
              {BNPL_PURCHASE_TYPE_OPTIONS.map((option, idx) => (
                <Radio
                  key={`bnpl-purchase-${option.value}`}
                  {...option}
                  idx={idx}
                  hasError={hasPurchaseTypeError}
                  name="bnplPurchaseType"
                  selectedValue={bnplPurchaseTypeValue ?? null}
                />
              ))}
            </RadioGroup>
            {hasPurchaseTypeError && <TextInputError>This field is required</TextInputError>}
          </div>
          <div>
            <RadioGroup
              label={EXTRA_QUESTION_LABELS.bnplLatePayments}
              hasError={hasLatePaymentsError}
              id="bnpl-late-payments-radio-group"
            >
              {BNPL_LATE_PAYMENTS_OPTIONS.map((option, idx) => (
                <Radio
                  key={`bnpl-late-${option.value}`}
                  {...option}
                  idx={idx}
                  hasError={hasLatePaymentsError}
                  name="bnplLatePayments"
                  selectedValue={bnplLatePaymentsValue ?? null}
                />
              ))}
            </RadioGroup>
            {hasLatePaymentsError && <TextInputError>This field is required</TextInputError>}
          </div>
        </>
      )}
    </div>
  )
}

export default BnplDetails
